/**
 * Chat service / LLM token API: validate, save, fetch, update, delete token details.
 */
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

const API_BASE_URL = API_CONFIG.API_BASE_URL;

/** Get auth token for authenticated requests */
const getAuthToken = (): string =>
  localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
  localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
  '';

// --- Request / Response types ---

export interface ValidateTokenPayload {
  llmType: string;
  llmApiKey: string;
  llmModelName?: string;
  llmApiVersion?: string;
  llmEndpoint?: string;
}

export interface ValidateTokenResponse {
  success: boolean;
  data: Record<string, unknown>;
  message: string;
  code: number;
}

export interface SaveTokenDetailsPayload {
  token_type: string;
  api_key: string;
  tokenName?: string;
  embedding_model?: string;
  gpt_model?: string;
  companyId: string;
  deploymentName?: string;
  deploymentVersion?: string;
  endpoint?: string;
}

export interface SaveTokenDetailsResponse {
  success: boolean;
  data: {
    vault_unique_id: string;
    giggso_vault_id: string;
  };
  message: string;
  code: number;
}

export interface FetchTokenDetailsPayload {
  companyId: string;
}

export interface TokenDetailItem {
  vault_unique_id?: string;
  giggso_vault_id?: string;
  token_type?: string;
  [key: string]: unknown;
}

export interface FetchTokenDetailsResponse {
  success: boolean;
  data: {
    vault_unique_id?: string;
    giggso_vault_id?: string;
    token_details: TokenDetailItem | TokenDetailItem[] | Record<string, unknown>;
  };
  message: string;
  code: number;
}

export interface UpdateTokenDetailsPayload {
  token_type: string;
  api_key: string;
  tokenName?: string;
  embedding_model?: string;
  gpt_model?: string;
  companyId: string;
  deploymentName?: string;
  deploymentVersion?: string;
  endpoint?: string;
  vault_unique_id?: string;
  giggso_vault_id?: string;
}

export interface UpdateTokenDetailsResponse {
  success: boolean;
  data: {
    vault_unique_id: string;
    giggso_vault_id: string;
  };
  message: string;
  code: number;
}

export interface DeleteTokenDetailsPayload {
  companyId: string;
  vault_unique_id?: string;
}

export interface DeleteTokenDetailsResponse {
  success: boolean;
  data: {
    deleted: boolean;
    vault_unique_id: string;
    giggso_vault_id: string;
  };
  message: string;
  code: number;
}

// --- API helpers ---

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (data as { message?: string }).message ||
      (data as { detail?: string }).detail ||
      `Request failed: ${response.status}`;
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return data as T;
}

/**
 * Validate LLM token via gpt-token-validation endpoint.
 * Throws if HTTP is not ok or if response body has success: false (e.g. quota_issue).
 */
export async function validateToken(
  payload: ValidateTokenPayload
): Promise<ValidateTokenResponse> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}${API_ENDPOINTS.CHAT_SERVICE.VALIDATE_TOKEN}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  const data = await response.json().catch(() => ({})) as ValidateTokenResponse & { data?: { message?: string } };
  // Helper: get user-facing message from API (top-level or nested in data.data)
  const getApiMessage = (fallback: string): string => {
    const fromData = data?.data && typeof data.data === 'object' && (data.data as { message?: string }).message;
    const fromTop = (data as { message?: string }).message;
    const msg = (typeof fromData === 'string' && fromData.trim() ? fromData : null) ?? (typeof fromTop === 'string' && fromTop.trim() ? fromTop : null);
    return msg ?? fallback;
  };
  // Treat HTTP error status as failure
  if (!response.ok) {
    const message = getApiMessage((data as { detail?: string }).detail || `Request failed: ${response.status}`);
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  // API can return 200 with success: false (e.g. quota_issue, validation failed)
  if (data.success === false) {
    const message = getApiMessage('Validation failed');
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message));
  }
  return data;
}

/**
 * Save token details (public endpoint; may not require auth in onboarding).
 */
export async function saveTokenDetails(
  payload: SaveTokenDetailsPayload
): Promise<SaveTokenDetailsResponse> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}${API_ENDPOINTS.CHAT_SERVICE.SAVE_TOKEN_DETAILS}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  return handleResponse<SaveTokenDetailsResponse>(response);
}

/**
 * Fetch token details for a company.
 */
export async function fetchTokenDetails(
  companyId: string
): Promise<FetchTokenDetailsResponse> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}${API_ENDPOINTS.CHAT_SERVICE.FETCH_TOKEN_DETAILS}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ companyId }),
    }
  );
  return handleResponse<FetchTokenDetailsResponse>(response);
}

/**
 * Update token details.
 */
export async function updateTokenDetails(
  payload: UpdateTokenDetailsPayload
): Promise<UpdateTokenDetailsResponse> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}${API_ENDPOINTS.CHAT_SERVICE.UPDATE_TOKEN_DETAILS}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  return handleResponse<UpdateTokenDetailsResponse>(response);
}

/**
 * Delete token details. Payload includes companyId; add vault_unique_id if backend supports per-token delete.
 */
export async function deleteTokenDetails(
  payload: DeleteTokenDetailsPayload
): Promise<DeleteTokenDetailsResponse> {
  const token = getAuthToken();
  const response = await fetch(
    `${API_BASE_URL}${API_ENDPOINTS.CHAT_SERVICE.DELETE_TOKEN_DETAILS}`,
    {
      method: 'DELETE',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  return handleResponse<DeleteTokenDetailsResponse>(response);
}
